// @flow

import React from 'react';
import moment from 'moment';
import { QueryRenderer, graphql } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { i18n } from 'shared/utils';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import Panel from 'shared/components/common/Panel';
import { Grid, MetricCard, MetricValue, MetricLabel, UpdatedAt, EmptyState } from './styled';

const Qc = () => (
  <Page>
    <PageHeader
      title={i18n.t('QC')}
      intro={i18n.t('A live summary from your connected QC organization')}
    />
    <QueryRenderer
      environment={relayEnvironment}
      cacheConfig={{ force: true }}
      query={graphql`
        query QcQuery {
          qcIntegrationEnabled
          qcAppUrl
          qcSummary {
            openInspectionsCount
            closedInspectionsLast30Days
            passRateLast30Days
            openIssuesCount
            generatedAt
          }
        }
      `}
      variables={{}}
      render={({ props, error }) => {
        if (error) {
          return (
            <Panel>
              <EmptyState>{i18n.t('Something went wrong loading QC data. Please try again.')}</EmptyState>
            </Panel>
          );
        }

        if (!props) {
          return <Loader />;
        }

        const { qcIntegrationEnabled, qcAppUrl, qcSummary } = props;

        if (!qcIntegrationEnabled) {
          return (
            <Panel title={i18n.t('QC is not connected')}>
              <EmptyState>
                {i18n.t(
                  "Connect your QC organization to see a live summary here. An admin can generate an API key in QC, then enter it under Settings > Integrations > QC.",
                )}
                <div style={{ paddingTop: 16 }}>
                  <Button theme="blue" width="auto" onClick={() => { window.location.href = '/settings/integrations'; }}>
                    {i18n.t('Go to Settings > Integrations')}
                  </Button>
                </div>
              </EmptyState>
            </Panel>
          );
        }

        return (
          <Panel
            title={i18n.t('Summary')}
            headerRight={
              <Button
                theme="blue"
                width="auto"
                onClick={() => window.open(qcAppUrl, '_blank', 'noopener,noreferrer')}
              >
                {i18n.t('Open QC')}
              </Button>
            }
          >
            {!qcSummary ? (
              <EmptyState>
                {i18n.t(
                  'QC could not be reached right now. Data will resume automatically once QC is available again.',
                )}
              </EmptyState>
            ) : (
              <>
                <Grid>
                  <MetricCard>
                    <MetricValue>{qcSummary.openInspectionsCount}</MetricValue>
                    <MetricLabel>{i18n.t('Open Inspections')}</MetricLabel>
                  </MetricCard>
                  <MetricCard>
                    <MetricValue>{qcSummary.closedInspectionsLast30Days}</MetricValue>
                    <MetricLabel>{i18n.t('Closed (Last 30 Days)')}</MetricLabel>
                  </MetricCard>
                  <MetricCard highlight={qcSummary.passRateLast30Days !== null && qcSummary.passRateLast30Days < 90}>
                    <MetricValue highlight={qcSummary.passRateLast30Days !== null && qcSummary.passRateLast30Days < 90}>
                      {qcSummary.passRateLast30Days !== null ? `${qcSummary.passRateLast30Days}%` : '—'}
                    </MetricValue>
                    <MetricLabel>{i18n.t('Pass Rate (Last 30 Days)')}</MetricLabel>
                  </MetricCard>
                  <MetricCard highlight={qcSummary.openIssuesCount > 0}>
                    <MetricValue highlight={qcSummary.openIssuesCount > 0}>
                      {qcSummary.openIssuesCount}
                    </MetricValue>
                    <MetricLabel>{i18n.t('Open Issues')}</MetricLabel>
                  </MetricCard>
                </Grid>
                <UpdatedAt>
                  {i18n.t('Updated {{time}}', { time: moment(qcSummary.generatedAt).fromNow() })}
                </UpdatedAt>
              </>
            )}
          </Panel>
        );
      }}
    />
  </Page>
);

export default Qc;
